import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

type Side = 'left' | 'right';

export function ResizeHandle({
  side,
  initialWidth,
  onChange,
  min = 180,
  max = 520,
}: {
  side: Side;
  initialWidth: number;
  onChange: (w: number) => void;
  min?: number;
  max?: number;
}) {
  const startX = useRef(0);
  const startW = useRef(0);
  const dragging = useRef(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - startX.current;
      const next = side === 'left' ? startW.current + dx : startW.current - dx;
      onChange(Math.max(min, Math.min(max, next)));
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [onChange, side, min, max]);

  const onDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startX.current = e.clientX;
    startW.current = initialWidth;
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onDown}
      onDoubleClick={() => onChange(side === 'left' ? 256 : 320)}
      title="Перетягніть для зміни ширини · подвійний клік — скинути"
      className={cn(
        'group relative w-1.5 shrink-0 cursor-col-resize select-none transition-colors',
        'bg-transparent hover:bg-blue-500/30'
      )}
    >
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white/5 group-hover:bg-blue-500/50 transition-colors" />
    </div>
  );
}
